import isEqual from 'lodash/isEqual';

import type { DocNode } from '@atlaskit/adf-schema';
import { transformContainerNodes } from '@atlaskit/adf-utils/transforms';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { validator } from '@atlaskit/adf-utils/validator';
import type { ErrorCallback, ValidationError } from '@atlaskit/adf-utils/validatorTypes';
// oxlint-disable-next-line import/no-duplicates
import type { Slice } from '@atlaskit/editor-prosemirror/model';
import type {
	ContentMatch,
	Mark,
	Node,
	NodeType,
	Schema,
} from '@atlaskit/editor-prosemirror/model';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
// eslint-disable-next-line no-restricted-imports
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { JsonRepair } from './json-repair';
import { repairNodeMarks } from './repair-using-validatorspec/mark';
import { validateAndRepairNodeAttrs } from './repair-using-validatorspec/node-attrs';
import { toSupportedNode } from './to-supported-node';
import type { ADFNode, PartialNode } from './types';

// Ignored via go/ees005
// eslint-disable-next-line require-unicode-regexp
const DIGITS_ONLY_REGEX = /^\d+$/;

type NodeTypeTree = { content?: NodeTypeTree[]; type?: string };

const isTextblock = (schema: Schema, typeName?: string) =>
	!!typeName && !!schema.nodes[typeName]?.isTextblock;

function isTextLink(node: ADFNode): boolean {
	const isText = node.type === 'text';
	if (!isText) {
		return false;
	}
	const mark = node.marks?.find((mark) => mark.type === 'link');
	return !!mark;
}

// these block nodes should be treated as leaf nodes which should be omitted
// because they have src or url attrs that can be null after repair
// We handle tables differently so we also add it to the list
const leafLikeNodeTypes = ['blockCard', 'embedCard', 'unsupportedBlock', 'tableRow'];

function countIncompleteRows(node: ADFNode): number {
	if (node.type !== 'table') {
		return 0;
	}

	const rows = node.content;
	if (!rows) {
		return 0;
	}

	//
	if (rows.length === 1) {
		return 1;
	}

	// Always ignore the final row if this is the end of the stream
	const rowsWithoutLast = rows.slice(0, rows.length - 1);

	let lastCompleteIndex = -1;
	let incompleteRowCount = 1;
	rowsWithoutLast.forEach((row, index) => {
		const maxMergedRows = (row.content || []).reduce((max, cell) => {
			return Math.max(max, typeof cell.attrs?.rowspan === 'number' ? cell.attrs.rowspan : 1);
		}, 1);

		incompleteRowCount = Math.max(incompleteRowCount, maxMergedRows);
		incompleteRowCount--;

		if (incompleteRowCount === 0) {
			lastCompleteIndex = index;
		}
	});

	/**
	 * If lastCompleteIndex is -1, it means that no complete row has been found even after
	 * trimming the last row.
	 *
	 * rowsWithoutLast.length - (-1) means that none of the rows are valid, so we actually
	 * return the full row length here by doing double negation.
	 */
	return rowsWithoutLast.length - lastCompleteIndex;
}

function isLeafLikeBlockNode(node: ADFNode): boolean {
	return (
		node.type.startsWith('media') ||
		node.type.startsWith('extension') ||
		node.type.endsWith('Extension') ||
		/**
		 * We want to treat tableRow as a leaf-like node so that we know cells are fully formed.
		 * This will avoid any insertion issues while table is being streamed.
		 */
		leafLikeNodeTypes.includes(node.type)
	);
}

function findLastLeafNode(doc: PartialNode) {
	let parent: ADFNode = doc as ADFNode;
	let leaf: ADFNode | undefined =
		doc.content && (doc.content[doc.content.length - 1] as ADFNode | undefined);

	while (leaf && !!leaf.content) {
		if (isLeafLikeBlockNode(leaf)) {
			break;
		}

		parent = leaf;
		leaf = leaf.content[leaf.content.length - 1];
	}

	return { parent, leaf };
}

type NodeMovedToEndOfPreviousNodeEvent = {
	childIndexInPreviousNode: number;
	movedNodeType: string;
	oldChildIndex: number;
	parentNodeType: string;
	previousNodeType: string;
};

type InvalidWrappingNodesEvent = {
	childIndex: number;
	childNodeType: string;
	currentChildNode: string;
	wrappingNodeType: string;
};

type WrappingNodesNotFoundEvent = {
	childIndex: number;
	childNodeType: string;
	parentNodeType: string;
};

type WrappingNodesFoundEvent = {
	childIndex: number;
	childNodeType: string;
	parentNodeType: string;
	wrappingNodeTypes: string[];
};

type ProcessStatus = {
	error: string;
	failed: boolean;
	firstFailedAtChunk: number;
	firstRecoveredAtChunk: number;
	lastFailedAtChunk: number;
	lastRecoveredAtChunk: number;
	recovered: boolean;
	stack?: string;
	validationError?: ValidationError;
};

type AnalyticsEventsSessionData = {
	adf: {
		failedToRepairADF: ProcessStatus;
		repairInvalidContent: {
			invalidWrappingNodesEvents: Array<InvalidWrappingNodesEvent>;
			nodeMovedToEndOfPreviousNodeEvents: Array<NodeMovedToEndOfPreviousNodeEvent>;
			wrappingNodesFoundEvents: Array<WrappingNodesFoundEvent>;
			wrappingNodesNotFoundEvents: Array<WrappingNodesNotFoundEvent>;
		};
		topNode: ProcessStatus;
		topNodeValidation: ProcessStatus;
	};
	chunksCount: number;
	json: {
		repairJson: ProcessStatus;
	};
};

/**
 * Generic function to mark a process as failed
 * @param analyticsEventsSessionData - The analytics session data
 * @param processStatus - The specific process status object to update
 * @param error - The error message to record
 */
function markProcessFailed({
	analyticsEventsSessionData,
	processStatus,
	error,
	stack,
	validationError,
}: {
	analyticsEventsSessionData: AnalyticsEventsSessionData;
	error: string;
	processStatus: ProcessStatus;
	stack?: string;
	validationError?: ValidationError;
}) {
	processStatus.failed = true;
	processStatus.error = error;
	processStatus.recovered = false;
	processStatus.stack = stack;
	processStatus.validationError = validationError;
	/**
	 * If multiple times process fails, only first and last failure will be recorded.
	 * If need arise in future, then we can record all failures.
	 */
	processStatus.lastFailedAtChunk = analyticsEventsSessionData.chunksCount;

	if (processStatus.firstFailedAtChunk === 0) {
		processStatus.firstFailedAtChunk = analyticsEventsSessionData.chunksCount;
	}
}

/**
 * Generic function to mark a process as recovered
 * @param analyticsEventsSessionData - The analytics session data
 * @param processStatus - The specific process status object to update
 */
function markProcessRecovered({
	analyticsEventsSessionData,
	processStatus,
}: {
	analyticsEventsSessionData: AnalyticsEventsSessionData;
	processStatus: ProcessStatus;
}) {
	/**
	 * If failed and not recovered then only mark it as recovered.
	 * If already recovered, then don't mark it as recovered again, otherwise recoveredAtChunk will have wrong value.
	 */
	if (processStatus.failed && !processStatus.recovered) {
		processStatus.recovered = true;
		processStatus.lastRecoveredAtChunk = analyticsEventsSessionData.chunksCount;

		if (processStatus.firstRecoveredAtChunk === 0) {
			processStatus.firstRecoveredAtChunk = analyticsEventsSessionData.chunksCount;
		}
	}
}

function markJsonRepairFailed(
	analyticsEventsSessionData: AnalyticsEventsSessionData,
	error: string,
) {
	markProcessFailed({
		analyticsEventsSessionData,
		processStatus: analyticsEventsSessionData.json.repairJson,
		error,
	});
}

function markJsonRepairRecovered(analyticsEventsSessionData: AnalyticsEventsSessionData) {
	markProcessRecovered({
		analyticsEventsSessionData,
		processStatus: analyticsEventsSessionData.json.repairJson,
	});
}

function markFailedToRepairADF(
	analyticsEventsSessionData: AnalyticsEventsSessionData,
	error: string,
	stack?: string,
) {
	markProcessFailed({
		analyticsEventsSessionData,
		processStatus: analyticsEventsSessionData.adf.failedToRepairADF,
		error,
		stack,
	});
}

function markFailedToRepairADFRecovered(analyticsEventsSessionData: AnalyticsEventsSessionData) {
	markProcessRecovered({
		analyticsEventsSessionData,
		processStatus: analyticsEventsSessionData.adf.failedToRepairADF,
	});
}

function markTopNodeFailed(analyticsEventsSessionData: AnalyticsEventsSessionData, error: string) {
	markProcessFailed({
		analyticsEventsSessionData,
		processStatus: analyticsEventsSessionData.adf.topNode,
		error,
	});
}

function markTopNodeRecovered(analyticsEventsSessionData: AnalyticsEventsSessionData) {
	markProcessRecovered({
		analyticsEventsSessionData,
		processStatus: analyticsEventsSessionData.adf.topNode,
	});
}

function markTopNodeValidationFailed(
	analyticsEventsSessionData: AnalyticsEventsSessionData,
	error: string,
	validationError?: ValidationError,
) {
	markProcessFailed({
		analyticsEventsSessionData,
		processStatus: analyticsEventsSessionData.adf.topNodeValidation,
		error,
		validationError,
	});
}

function markTopNodeValidationRecovered(analyticsEventsSessionData: AnalyticsEventsSessionData) {
	markProcessRecovered({
		analyticsEventsSessionData,
		processStatus: analyticsEventsSessionData.adf.topNodeValidation,
	});
}

const processStatusDefault = {
	failed: false,
	error: '',
	recovered: false,
	firstFailedAtChunk: 0,
	firstRecoveredAtChunk: 0,
	lastFailedAtChunk: 0,
	lastRecoveredAtChunk: 0,
};
const createDefaultAnalyticsEventsSessionData = (): AnalyticsEventsSessionData => ({
	chunksCount: 0,
	json: {
		repairJson: Object.assign({}, processStatusDefault),
	},
	adf: {
		topNode: Object.assign({}, processStatusDefault),
		repairInvalidContent: {
			wrappingNodesFoundEvents: [],
			nodeMovedToEndOfPreviousNodeEvents: [],
			invalidWrappingNodesEvents: [],
			wrappingNodesNotFoundEvents: [],
		},
		failedToRepairADF: Object.assign({}, processStatusDefault),
		topNodeValidation: Object.assign({}, processStatusDefault),
	},
});

function isEventForRepairInvalidContentExists(
	eventsRegistry: AnalyticsEventsSessionData['adf']['repairInvalidContent'],
	eventType:
		| 'nodeMovedToEndOfPreviousNodeEvents'
		| 'invalidWrappingNodesEvents'
		| 'wrappingNodesNotFoundEvents'
		| 'wrappingNodesFoundEvents',
	event:
		| NodeMovedToEndOfPreviousNodeEvent
		| InvalidWrappingNodesEvent
		| WrappingNodesNotFoundEvent
		| WrappingNodesFoundEvent,
) {
	return eventsRegistry[eventType].some((e) => isEqual(e, event));
}

export class ADFStreamer {
	private jsonRepair: JsonRepair;
	private schema: Schema;
	private lastDoc: DocNode;
	private fireAnalyticEventCallback: FireAnalyticEventCallback | undefined;
	private analyticsEventMetaData: Record<string, string | boolean | number> | undefined;
	private inputDocumentTree: NodeTypeTree | undefined;

	private analyticsEventsSessionData: AnalyticsEventsSessionData =
		createDefaultAnalyticsEventsSessionData();

	constructor(schema: Schema, fireAnalyticEvent?: FireAnalyticEventCallback) {
		this.jsonRepair = new JsonRepair();
		this.schema = schema;
		this.fireAnalyticEventCallback = fireAnalyticEvent;
		this.lastDoc = this.createDefaultDoc();
		this.inputDocumentTree = undefined;
	}

	public setAnalyticsEventMetaData(
		analyticsEventMetaData: Record<string, string | boolean | number>,
	): void {
		this.analyticsEventMetaData = analyticsEventMetaData;
	}
	private fireAnalyticEvent(event: AnalyticEventArgs) {
		event.data = { ...event.data, ...this.analyticsEventMetaData };
		this.fireAnalyticEventCallback?.(event);
	}

	public setInputDocumentTree(slice: Slice | undefined): NodeTypeTree | undefined {
		if (!slice || slice.size === 0) {
			return;
		}
		const empty = this.schema.topNodeType.createAndFill();
		if (!empty) {
			return;
		}

		try {
			const tr = new Transform(empty).replaceRange(0, empty.content.size, slice);
			const json = tr.doc.toJSON();
			const tree = this.getPartialNodeTypeTree(json);
			this.inputDocumentTree = tree;
		} catch {}
	}

	public reset(): void {
		this.jsonRepair.reset();
		this.lastDoc = this.createDefaultDoc();
		this.analyticsEventMetaData = undefined;
		this.analyticsEventsSessionData = createDefaultAnalyticsEventsSessionData();
	}

	public isInErrorState(): boolean {
		const isRepairJsonFailed =
			this.analyticsEventsSessionData.json.repairJson.failed &&
			!this.analyticsEventsSessionData.json.repairJson.recovered;
		const isFailedToRepairADF =
			this.analyticsEventsSessionData.adf.failedToRepairADF.failed &&
			!this.analyticsEventsSessionData.adf.failedToRepairADF.recovered;
		const isInvalidTopNode =
			this.analyticsEventsSessionData.adf.topNode.failed &&
			!this.analyticsEventsSessionData.adf.topNode.recovered;
		const isTopNodeValidationFailed =
			this.analyticsEventsSessionData.adf.topNodeValidation.failed &&
			!this.analyticsEventsSessionData.adf.topNodeValidation.recovered;

		const result = this.isEmptyLastDoc();
		const emptyLastDoc = result.isEmpty || !!result.deserializationError;
		if (result.deserializationError) {
			this.fireAnalyticEvent({
				type: 'error',
				message: 'Deserializing last doc to node failed in isInErrorState',
				data: { error: result.deserializationError },
			});
		}
		return (
			isRepairJsonFailed ||
			isFailedToRepairADF ||
			isInvalidTopNode ||
			isTopNodeValidationFailed ||
			emptyLastDoc
		);
	}

	private createDefaultDoc() {
		const defaultDoc = this.schema.topNodeType.createAndFill()?.toJSON();
		return { ...defaultDoc, version: 1 } as DocNode;
	}

	/**
	 * This method is used to append a chunk to the streamer.
	 * It will not process the chunk, you need to call {@link ADFStreamer.processChunk} to process the chunk.
	 *
	 * This is usefull when you just want to append a chunk to the streamer without processing it.
	 *
	 * @param chunk - The chunk to append.
	 */
	public appendChunk(chunk: string): void {
		this.jsonRepair.append(chunk);
	}

	/**
	 * This method is used to append and process a chunk.
	 * @param chunk - The chunk to append and process.
	 * @returns The streamer itself.
	 */
	public processChunk(chunk: string): ADFStreamer {
		this.analyticsEventsSessionData.chunksCount++;
		this.jsonRepair.append(chunk);

		try {
			const { hasChanged, repairedJson } = this.jsonRepair.parse();
			if (hasChanged) {
				this.repairADFJson(repairedJson);
			}
			markJsonRepairRecovered(this.analyticsEventsSessionData);
		} catch (jsonRepairError) {
			markJsonRepairFailed(
				this.analyticsEventsSessionData,
				jsonRepairError instanceof Error ? jsonRepairError.message : 'Unknown error',
			);
		}

		return this;
	}

	/**
	 * This method is used to get the last document without the last leaf node.
	 * Because the last leaf node may not be complete yet and it will be repaired in the next stream.
	 * You still need to call {@link ADFStreamer.getLastDoc} to get the last document after processing the last stream.
	 *
	 * @example
	 *
	 * // for loading
	 * streamer.processChunk(stream);
	 * streamer.getLastDocWithoutLastLeafNode();
	 *
	 * // for preview
	 * streamer.getLastDoc();
	 *
	 * @returns The last document without the last leaf node.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public getLastDocWithoutLastLeafNode(): any {
		const doc = this.createCopyOfDoc(this.lastDoc);
		const { parent, leaf } = findLastLeafNode(doc);

		// bailout if empty content
		if (!leaf) {
			return this.createCopyOfDoc(doc);
		}

		// only keep text leaf node if it is not a link
		const keepTextLeaf = leaf && !!leaf.text && !isTextLink(leaf);

		if (
			// empty Paragraph
			isTextblock(this.schema, leaf.type) ||
			keepTextLeaf
		) {
			return this.createCopyOfDoc(doc);
		}

		if (parent.type === 'table' && leaf.type === 'tableRow') {
			/**
			 * For tables, we want to analyse whether or not the rows are actually complete.
			 * This includes checking rows with merged cells.
			 * We trim any incomplete rows at the end of the table to maintain structural integrity when streaming.
			 */
			const incompleteRows = countIncompleteRows(parent);
			if (!!parent.content) {
				parent.content = parent.content.slice(0, parent.content.length - incompleteRows);
			}
		} else {
			// being cautious here, because it may break the ADF if arbitrarily remove the last leaf node from any parent node
			// - remove last leaf node from paragraph-like parent node
			// - remove block node if its a leaf-like node
			const shouldRemoveLastLeaf =
				isLeafLikeBlockNode(leaf) || isTextblock(this.schema, parent.type);
			if (!!parent.content && parent.content.length > 0 && shouldRemoveLastLeaf) {
				parent.content = parent.content.slice(0, parent.content.length - 1);
			}
		}

		return this.createCopyOfDoc(doc);
	}

	public getLastDoc(): DocNode {
		return this.createCopyOfDoc(this.lastDoc);
	}

	private createCopyOfDoc(doc: DocNode) {
		/**
		 * Some older browsers like chrome 97 doesn't support structuredClone.
		 * So using JSON.parse(JSON.stringify()) as a fallback.
		 */
		if (typeof window !== 'undefined' && typeof window.structuredClone === 'function') {
			return structuredClone(doc);
		} else {
			return JSON.parse(JSON.stringify(doc));
		}
	}

	private repairADFJson(partialJson: PartialNode) {
		/**
		 * No need to wait till we get "type: doc" in the JSON.
		 * First node type will always be schema.topNodeType, and can start repairing
		 * 	the JSON as soon as there is content in it.
		 */
		const sanitizedTopNodeResult = this.sanitizeTopNode(partialJson);
		if (sanitizedTopNodeResult.failed) {
			markTopNodeFailed(this.analyticsEventsSessionData, sanitizedTopNodeResult.error);
			return this.lastDoc;
		}

		if (this.analyticsEventsSessionData.adf.topNode.failed) {
			markTopNodeRecovered(this.analyticsEventsSessionData);
		}

		const sanitizedTopNode = sanitizedTopNodeResult.sanitizedTopNode;
		try {
			const docRepairResult = this.repairNode({
				partialNodeJson: sanitizedTopNode,
				isTopNode: true,
			});

			/**
			 * repairNode will be undefined if the node cannot be repaired
			 * We only update lastDoc if node is repaired successfully
			 */

			if (docRepairResult && docRepairResult.node) {
				const currentDoc = docRepairResult.node;

				const { result, error } = this.validateNodeUsingValidator(currentDoc);
				if (result) {
					this.lastDoc = currentDoc.toJSON() as DocNode;
					markTopNodeValidationRecovered(this.analyticsEventsSessionData);
				} else {
					markTopNodeValidationFailed(
						this.analyticsEventsSessionData,
						error?.errorMessage || 'Invalid top node',
						error?.validationError,
					);
				}

				markFailedToRepairADFRecovered(this.analyticsEventsSessionData);
			}
		} catch (e) {
			markFailedToRepairADF(
				this.analyticsEventsSessionData,
				e instanceof Error ? e.message : 'Unknown error',
				e instanceof Error ? e.stack : undefined,
			);
		}

		return this.lastDoc;
	}

	private sanitizeTopNode(
		partialNodeJson: PartialNode,
	): { error: string; failed: true } | { failed: false; sanitizedTopNode: PartialNode } {
		let sanitizedTopNode = partialNodeJson;
		if (Array.isArray(partialNodeJson)) {
			/**
			 * We have observed that LLM sometimes returns invalid adf json
			 * 	or sometimes returns top partial node as an array.
			 * For example, LLM forgets put curly bracket to close node,
			 * 	and when jsonrepair repairs it, it will change top node to be an array.
			 * And doc node becomes part of an array along with other nodes.
			 *
			 * So if we find top partialNodeJson as an array and found topNode (doc)
			 * 	in array then move other nodes to topNode and return topNode.
			 */
			sanitizedTopNode = partialNodeJson.find(
				(node) => node.type === this.schema.topNodeType.name,
			) as PartialNode;
			if (sanitizedTopNode) {
				const otherNodes = partialNodeJson.filter(
					(node) => node.type !== this.schema.topNodeType.name,
				);
				sanitizedTopNode.content = [...(sanitizedTopNode.content || []), ...otherNodes];
			} else {
				return { failed: true, error: 'Valid top node not found in array.' };
			}
		} else if (typeof partialNodeJson !== 'object' || partialNodeJson === null) {
			return { failed: true, error: 'Invalid top node.' };
		}

		try {
			if (expValEquals('platform_editor_nest_table_in_panel', 'isEnabled', true)) {
				const { isTransformed, transformedAdf } = transformContainerNodes(
					sanitizedTopNode as ADFEntity,
					this.schema,
				);
				if (isTransformed && transformedAdf) {
					sanitizedTopNode = transformedAdf as PartialNode;
				}
			}
		} catch (e) {
			return {
				failed: true,
				error: `Error transforming container nodes: ${e instanceof Error ? e.message : 'Unknown error'}`,
			};
		}

		return { failed: false, sanitizedTopNode };
	}

	private repairNode(
		...args: Parameters<typeof this.repairNodeOld>
	): ReturnType<typeof this.repairNodeOld> {
		return fg('platform_editor_ai_refactor_repair_method')
			? this.repairNodeNew(...args)
			: this.repairNodeOld(...args);
	}

	private repairNodeOld({
		partialNodeJson,
		parentNodeType,
		isTopNode,
	}: {
		isTopNode?: boolean;
		parentNodeType?: NodeType;
		partialNodeJson: PartialNode;
	}): { invalidNodes: Node[]; node: Node | null | undefined } | undefined {
		const type = this.inferNodeType({ partialNodeJson, parentNodeType, isTopNode });

		if (!type) {
			// If type can't be inferred, then we can't repair the node, so skipping it.
			this.fireAnalyticEvent({
				type: 'info',
				message: 'Failed to infer node type.',
				data: {
					nodeType: partialNodeJson.type ?? 'type is undefined',
				},
			});
			return;
		}

		let marksToUse = partialNodeJson.marks;
		const repairedMarksResult = repairNodeMarks(type.name, marksToUse);
		if (repairedMarksResult.repairedMarks && repairedMarksResult.repairedMarks.length > 0) {
			marksToUse = repairedMarksResult.repairedMarks;
		}
		if (repairedMarksResult.errors && repairedMarksResult.errors.length > 0) {
			// EDITOR-2316 Send analytic event when repairing of mark happens.
			// this.fireAnalyticEvent({
			// 	type: 'info',
			// 	message: 'Failed to repair marks of node.',
			// 	data: { nodeType: type.name, errors: repairedMarksResult.errors },
			// });
		}

		const maybeMarks = marksToUse
			? marksToUse
					.map((mark) => this.createMark(mark))
					// Type guard isn't necessary in newer versions of TypeScript
					.filter((mark: Mark | null): mark is Mark => mark !== null)
			: [];

		if (type.name === 'text') {
			/**
			 * Empty text node is not allowed in prosemirror.
			 * Checkout: https://prosemirror.net/docs/ref/#model.Schema.text
			 * That's why when text is not available, we are using space as default.
			 */
			return {
				node: this.schema.text(partialNodeJson.text ? partialNodeJson.text : ' ', maybeMarks),
				invalidNodes: [],
			};
		}

		if (type.name === 'date') {
			const today = new Date().getTime().toString();
			partialNodeJson.attrs = partialNodeJson.attrs || {};
			if (partialNodeJson.attrs.timestamp) {
				if (typeof partialNodeJson.attrs.timestamp === 'string') {
					if (partialNodeJson.attrs.timestamp.includes('-')) {
						const d = new Date(partialNodeJson.attrs.timestamp as string);
						partialNodeJson.attrs.timestamp = isNaN(d.getTime()) ? today : d.getTime().toString();
					} else if (!String(partialNodeJson.attrs.timestamp).match(DIGITS_ONLY_REGEX)) {
						partialNodeJson.attrs.timestamp = today;
					}
				}
			} else {
				partialNodeJson.attrs.timestamp = today;
			}
		}

		if (type.name === 'panel' && partialNodeJson.attrs?.panelType === 'tip') {
			partialNodeJson.attrs.panelType = 'info';
		}

		const maybeContent = partialNodeJson.content
			? this.repairContent({ newContent: partialNodeJson.content, parentNodeType: type })
					.filter((node) => !!node)
					.map((node) => this.schema.nodeFromJSON(node))
			: undefined;

		let attrsToUse = partialNodeJson.attrs;
		if (fg('platform_editor_ai_repair_attrs_with_validatorspec')) {
			// EDITOR-2316 Send analytic event when repairing of mark happens.
			const repairedAttrsResult = validateAndRepairNodeAttrs(type.name, attrsToUse);
			if (repairedAttrsResult.isValid) {
				attrsToUse = repairedAttrsResult.repairedAttrs;
			}
		}

		let node = type.createAndFill(attrsToUse, maybeContent, maybeMarks);
		let invalidNodes: Node[] = [];
		/**
		 * If node is null, then it means content is invalid and fitting wrapping node is not found.
		 * We need to repair the content and try again.
		 */
		if (!node && maybeContent) {
			const { repairedContent, invalidNodes: invalidNodesFromRepairInvalidContent } =
				this.repairInvalidContent({ parentNodeType: type, maybeContent });
			invalidNodes = invalidNodesFromRepairInvalidContent;
			node = type.createAndFill(attrsToUse, repairedContent, maybeMarks);
			if (!node) {
				/**
				 * Tried to repair content, but still failed. So need to skip this node.
				 */
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Tried to repair content of node, but failed.',
					data: {
						nodeType: type.name ?? 'type is undefined',
						repairedContentNodeTypeTree: repairedContent.map((node) => this.getNodeTypeTree(node)),
						originalNodeTypeTree: this.getPartialNodeTypeTree(partialNodeJson),
					},
				});
				return;
			}
		}

		return { node, invalidNodes };
	}

	private repairNodeNew({
		partialNodeJson,
		parentNodeType,
		isTopNode,
	}: {
		isTopNode?: boolean;
		parentNodeType?: NodeType;
		partialNodeJson: PartialNode;
	}): { invalidNodes: Node[]; node: Node | null | undefined } | undefined {
		const type = this.inferNodeType({ partialNodeJson, parentNodeType, isTopNode });

		if (!type) {
			// If type can't be inferred, then we can't repair the node, so skipping it.
			this.fireAnalyticEvent({
				type: 'info',
				message: 'Failed to infer node type.',
				data: {
					nodeType: partialNodeJson.type ?? 'type is undefined',
				},
			});
			return;
		}

		let marksToUse = partialNodeJson.marks;
		const repairedMarksResult = repairNodeMarks(type.name, marksToUse);
		if (repairedMarksResult.repairedMarks && repairedMarksResult.repairedMarks.length > 0) {
			marksToUse = repairedMarksResult.repairedMarks;
		}
		if (repairedMarksResult.errors && repairedMarksResult.errors.length > 0) {
			// EDITOR-2316 Send analytic event when repairing of mark happens.
			// this.fireAnalyticEvent({
			// 	type: 'info',
			// 	message: 'Failed to repair marks of node.',
			// 	data: { nodeType: type.name, errors: repairedMarksResult.errors },
			// });
		}

		const maybeMarks = marksToUse
			? marksToUse
					.map((mark) => this.createMark(mark))
					// Type guard isn't necessary in newer versions of TypeScript
					.filter((mark: Mark | null): mark is Mark => mark !== null)
			: [];

		if (type.name === 'text') {
			/**
			 * Empty text node is not allowed in prosemirror.
			 * Checkout: https://prosemirror.net/docs/ref/#model.Schema.text
			 * That's why when text is not available, we are using space as default.
			 */
			return {
				node: this.schema.text(partialNodeJson.text ? partialNodeJson.text : ' ', maybeMarks),
				invalidNodes: [],
			};
		}

		const maybeContent = partialNodeJson.content
			? this.repairContent({ newContent: partialNodeJson.content, parentNodeType: type })
					.filter((node) => !!node)
					.map((node) => this.schema.nodeFromJSON(node))
			: undefined;

		let validAttrs = partialNodeJson.attrs;
		if (fg('platform_editor_ai_repair_attrs_with_validatorspec')) {
			// EDITOR-2316 Send analytic event when repairing of mark happens.
			const repairedAttrsResult = validateAndRepairNodeAttrs(type.name, validAttrs);
			if (repairedAttrsResult.isValid) {
				validAttrs = repairedAttrsResult.repairedAttrs;
			}
		}

		const validNode = { ...partialNodeJson, attrs: validAttrs };
		const supportedNode = toSupportedNode(type.name, validNode);

		let node = type.createAndFill(supportedNode.attrs, maybeContent, maybeMarks);
		let invalidNodes: Node[] = [];
		/**
		 * If node is null, then it means content is invalid and fitting wrapping node is not found.
		 * We need to repair the content and try again.
		 */
		if (!node && maybeContent) {
			const { repairedContent, invalidNodes: invalidNodesFromRepairInvalidContent } =
				this.repairInvalidContent({ parentNodeType: type, maybeContent });
			invalidNodes = invalidNodesFromRepairInvalidContent;
			node = type.createAndFill(supportedNode.attrs, repairedContent, maybeMarks);
			if (!node) {
				/**
				 * Tried to repair content, but still failed. So need to skip this node.
				 */
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Tried to repair content of node, but failed.',
					data: {
						nodeType: type.name ?? 'type is undefined',
						repairedContentNodeTypeTree: repairedContent.map((node) => this.getNodeTypeTree(node)),
						originalNodeTypeTree: this.getPartialNodeTypeTree(supportedNode),
					},
				});
				return;
			}
		}

		return { node, invalidNodes };
	}

	private repairInvalidContent({
		parentNodeType,
		maybeContent,
	}: {
		maybeContent: Node[];
		parentNodeType: NodeType;
	}) {
		let currentContentMatch: ContentMatch = parentNodeType.contentMatch;
		const invalidNodes: Node[] = [];
		const repairedContent: Node[] = [];
		maybeContent.forEach((node, index) => {
			const nextContentMatch = currentContentMatch.matchType(node.type);
			if (nextContentMatch) {
				/**
				 * this node is allowed in the parent node.
				 * so continue to the next node.
				 */
				currentContentMatch = nextContentMatch;
				repairedContent.push(node);
			} else {
				const { taskItem, paragraph } = this.schema.nodes;
				/**
				 * The LLM has been observed to occasionally wrap taskItem content inside a paragraph.
				 * Task items support the same content as paragraph so we unwrap the pargaraph into the taskItem..
				 */
				if (parentNodeType === taskItem && node.type === paragraph) {
					repairedContent.push(...node.content.content);
					return;
				}

				/**
				 * We've noticed that the LLM occasionally omits closing } or ] brackets,
				 * 	causing the node to be incorrectly moved from its parent and placed as a sibling.
				 *
				 * If node is not valid at this position, then check if it is valid child
				 * 	at end in previous node. If it is, then move it to the end of previous node.
				 *
				 * Use repairedContent instead of maybeContent to avoid duplicate nodes.
				 */
				if (index > 0 && repairedContent.length > 0) {
					const previousNode = repairedContent[repairedContent.length - 1];
					const previousNodeChildCount = previousNode.childCount;
					if (
						previousNode.canReplaceWith(previousNodeChildCount, previousNodeChildCount, node.type)
					) {
						const previousNodeContent = previousNode.content;
						const newPreviousNodeContent = previousNodeContent.addToEnd(node);
						const newPreviousNode = previousNode.copy(newPreviousNodeContent);
						repairedContent[repairedContent.length - 1] = newPreviousNode;

						/**
						 * During streaming, invalid content will be repaired on processing every chunk.
						 * Without checking if event already exists, it will fire multiple events for same node.
						 * With duplicate events, analyzing the data will be difficult.
						 */
						const event: NodeMovedToEndOfPreviousNodeEvent = {
							parentNodeType: parentNodeType.name,
							previousNodeType: previousNode.type.name,
							movedNodeType: node.type.name,
							oldChildIndex: index,
							childIndexInPreviousNode: previousNodeContent.childCount,
						};
						if (
							!isEventForRepairInvalidContentExists(
								this.analyticsEventsSessionData.adf.repairInvalidContent,
								'nodeMovedToEndOfPreviousNodeEvents',
								event,
							)
						) {
							this.analyticsEventsSessionData.adf.repairInvalidContent.nodeMovedToEndOfPreviousNodeEvents.push(
								event,
							);
						}

						return;
					}
				}
				/**
				 * this node is not allwed directly in parent node.
				 * so let's check if we wrap this node with other nodes, it can be allowed or not.
				 */
				const wrappingNodeTypes = currentContentMatch.findWrapping(node.type);
				let currentChildNode = node;
				if (wrappingNodeTypes) {
					for (let i = wrappingNodeTypes?.length - 1; i >= 0; i--) {
						const wrappingNodeType = wrappingNodeTypes[i];
						const nextChildNode = wrappingNodeType.createAndFill(null, [currentChildNode]);
						if (nextChildNode) {
							currentChildNode = nextChildNode;
						} else {
							/**
							 * This should not happen as prosemirror should never give us such a wrappingNodes
							 * so that createAndFill fails.
							 * Buf it this happens, we need to fire analytic event, so that we diagnose the issue.
							 */
							const event: InvalidWrappingNodesEvent = {
								childNodeType: node.type.name,
								childIndex: index,
								wrappingNodeType: wrappingNodeType.name,
								currentChildNode: currentChildNode.type.name,
							};
							if (
								!isEventForRepairInvalidContentExists(
									this.analyticsEventsSessionData.adf.repairInvalidContent,
									'invalidWrappingNodesEvents',
									event,
								)
							) {
								this.analyticsEventsSessionData.adf.repairInvalidContent.invalidWrappingNodesEvents.push(
									event,
								);
							}
							return;
						}
					}
					const event: WrappingNodesFoundEvent = {
						childNodeType: node.type.name,
						childIndex: index,
						wrappingNodeTypes: wrappingNodeTypes.map((nodeType) => nodeType.name),
						parentNodeType: parentNodeType.name,
					};
					if (
						!isEventForRepairInvalidContentExists(
							this.analyticsEventsSessionData.adf.repairInvalidContent,
							'wrappingNodesFoundEvents',
							event,
						)
					) {
						this.analyticsEventsSessionData.adf.repairInvalidContent.wrappingNodesFoundEvents.push(
							event,
						);
					}
					repairedContent.push(currentChildNode);
				} else {
					/**
					 * this node is not allowed in the parent node as child at any level.
					 * For example, a table node can't be a child of a bulletList node at any level.
					 *
					 * This can happen because sometimes LLM gives us a node that is not allowed in the parent node.
					 * Or LLM gives us invalid json, and jsonrepair repairs it in such a way that
					 * 	node is shifted and placed in a place where it's not allowed in the parent node.
					 *
					 * So we need to discard this node.
					 */
					const event: WrappingNodesNotFoundEvent = {
						childNodeType: node.type.name,
						parentNodeType: parentNodeType.name,
						childIndex: index,
					};
					if (
						!isEventForRepairInvalidContentExists(
							this.analyticsEventsSessionData.adf.repairInvalidContent,
							'wrappingNodesNotFoundEvents',
							event,
						)
					) {
						this.analyticsEventsSessionData.adf.repairInvalidContent.wrappingNodesNotFoundEvents.push(
							event,
						);
					}
					invalidNodes.push(node);
				}
			}
		});

		return { repairedContent: repairedContent.filter((node) => !!node), invalidNodes };
	}

	private inferNodeType({
		partialNodeJson,
		parentNodeType,
		isTopNode,
	}: {
		isTopNode?: boolean;
		parentNodeType?: NodeType;
		partialNodeJson: PartialNode;
	}): NodeType | null | undefined {
		/**
		 * If type is still not available, and, node has been previously repaired,
		 * then just use type of the repaired node.
		 * And if node hasn't been repaired, then use default type based on parent node type.
		 */
		let type: NodeType | null | undefined = null;
		if (partialNodeJson.type) {
			type = this.schema.nodes[partialNodeJson.type];
		}
		if (!type) {
			if (isTopNode) {
				type = this.schema.topNodeType;
			} else if (
				isTextblock(this.schema, parentNodeType?.name) ||
				/**
				 * codeBlock nodeType returns "unsupportedInline" as defaultType.
				 * Because prosemirror never returns text node as defaultType.
				 * So overriding "text" as defaultType for codeBlock.
				 */
				parentNodeType?.name === 'codeBlock' ||
				/**
				 * If node has text as child, then use text as defaultType.
				 */
				(typeof partialNodeJson === 'object' && 'text' in partialNodeJson)
			) {
				type = this.schema.nodes.text;
			}
		}
		return type || parentNodeType?.contentMatch.defaultType;
	}

	private repairContent({
		newContent,
		parentNodeType,
	}: {
		newContent: PartialNode[];
		parentNodeType: NodeType;
	}): ADFNode[] {
		const repairedContent: ADFNode[] = [];
		newContent.forEach((node, index) => {
			/**
			 * If repairing node fails, then instead of failing processing whole document,
			 * 	just skip the node and continue with the next node.
			 */
			try {
				const nodeRepairResult = this.repairNode({ partialNodeJson: node, parentNodeType });
				if (nodeRepairResult?.node) {
					repairedContent.push(nodeRepairResult.node.toJSON());
				}
				if (nodeRepairResult?.invalidNodes && nodeRepairResult?.invalidNodes.length > 0) {
					repairedContent.push(...nodeRepairResult.invalidNodes.map((node) => node.toJSON()));
				}
			} catch (e) {
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Failed to repair node',
					data: {
						error: e instanceof Error ? e.message : 'Unknown error',
						stack: e instanceof Error ? e.stack : undefined,
						nodeType: node.type ?? 'type is undefined',
						parentNodeType: parentNodeType.name,
						childIndex: index,
					},
				});
			}
		});
		return repairedContent;
	}

	private validateNodeUsingValidator(
		node: Node,
		parentNodeType?: NodeType,
	): {
		entity?: ADFEntity;
		error?: { errorMessage?: string; nodeType: string; validationError?: ValidationError };
		result: boolean;
	} {
		const nodes = Object.keys(this.schema.nodes);
		const marks = Object.keys(this.schema.marks);
		const validate = validator(nodes, marks, {
			allowPrivateAttributes: true,
			mode: 'strict',
			stage0: fg('platform_editor_adf_validator_stage0'),
		});
		try {
			const isNestedTable =
				node.type.name === 'table' &&
				(parentNodeType?.name === 'tableCell' || parentNodeType?.name === 'tableHeader');
			if (isNestedTable) {
				return { result: true };
			}
			let error:
				| { errorMessage?: string; nodeType: string; validationError?: ValidationError }
				| undefined;
			const errorCallback: ErrorCallback = (entity, validationError) => {
				error = {
					nodeType: entity.type ?? 'type is undefined',
					validationError,
				};
				return undefined;
			};
			const validation = validate(
				{
					...node?.toJSON(),
					/**
					 * In the ProseMirror model the doc version is a number, but
					 * ADF validator expects a string. We override this manually here as the
					 * version number is trivial to doc formation.
					 */
					...(node.type.name === 'doc' ? { version: '1' } : {}),
				},
				errorCallback,
			);

			if (validation.valid) {
				return { result: true, entity: validation.entity, error };
			}
			return { result: false, error };
		} catch (e) {
			return {
				result: false,
				error: {
					nodeType: node.type.name,
					errorMessage: e instanceof Error ? e.message : 'Unknown error',
				},
			};
		}
	}

	private createMark(json: PartialNode): Mark | null {
		if (json.type) {
			const type = this.schema.marks[json.type];
			if (!type) {
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Failed to repair mark, type not found',
					data: { markType: json.type },
				});
				return null;
			}
			return type.create(json.attrs);
		}
		return null;
	}

	private getPartialNodeTypeTree(node: PartialNode): NodeTypeTree {
		const children = node.content;

		return {
			type: node.type,
			content: Array.isArray(children)
				? children.map((child) => this.getPartialNodeTypeTree(child))
				: [],
		};
	}

	private getNodeTypeTree(node: Node): NodeTypeTree {
		const children = node.content;

		return {
			type: node.type.name,
			content: Array.isArray(children) ? children.map((child) => this.getNodeTypeTree(child)) : [],
		};
	}
	private isEmptyLastDoc() {
		try {
			const docNode = this.schema.nodeFromJSON(this.lastDoc);
			return {
				isEmpty:
					// If doc has no child then it is empty.
					docNode.childCount <= 1 &&
					// By default, lastDoc has empty paragraph as child of doc node.
					docNode.firstChild?.type.name === 'paragraph' &&
					docNode.firstChild?.childCount === 0,
				deserializationError: undefined,
			};
		} catch (error) {
			return {
				isEmpty: false,
				deserializationError: error instanceof Error ? error.message : 'Unknown error',
			};
		}
	}

	public endStreamingSession({
		hasProcessedFinalResponse,
	}: {
		hasProcessedFinalResponse?: boolean;
	} = {}):
		| {
				finalAdfInvalid: boolean;
				finalAdfRepaired: boolean;
				finalJsonInvalid: boolean;
				finalJsonRepaired: boolean;
		  }
		| undefined {
		this.fireAnalyticEvent({
			type: 'info',
			message: 'Number of chunks processed',
			data: {
				chunksCount: this.analyticsEventsSessionData.chunksCount,
				inputDocumentTree: this.inputDocumentTree,
			},
		});

		/**
		 * When input to LLM is very small, like couple of words,
		 * then few stream events are sent which has empty doc,
		 * and final response completes doc with transformed couple of words.
		 *
		 * So firing empty doc event only when final response is processed.
		 */

		if (hasProcessedFinalResponse) {
			const result = this.isEmptyLastDoc();
			if (result.isEmpty) {
				this.fireAnalyticEvent({
					type: 'error',
					message: 'Top node is empty',
					data: {
						inputDocumentTree: this.inputDocumentTree,
					},
				});
			}
			if (result.deserializationError) {
				this.fireAnalyticEvent({
					type: 'error',
					message: 'Deserializing last doc to node failed',
					data: {
						error: result.deserializationError,
					},
				});
			}
		}

		if (this.analyticsEventsSessionData.json.repairJson.failed) {
			this.fireAnalyticEvent({
				type: 'info',
				message: 'Failed to repair partial JSON using jsonrepair',
				data: {
					error: this.analyticsEventsSessionData.json.repairJson.error,
					firstFailedAtChunk: this.analyticsEventsSessionData.json.repairJson.firstFailedAtChunk,
					lastFailedAtChunk: this.analyticsEventsSessionData.json.repairJson.lastFailedAtChunk,
					recovered: this.analyticsEventsSessionData.json.repairJson.recovered,
					firstRecoveredAtChunk:
						this.analyticsEventsSessionData.json.repairJson.firstRecoveredAtChunk,
					lastRecoveredAtChunk:
						this.analyticsEventsSessionData.json.repairJson.lastRecoveredAtChunk,
				},
			});

			if (!this.analyticsEventsSessionData.json.repairJson.recovered) {
				this.fireAnalyticEvent({
					type: 'error',
					message: 'Received invalid json',
					data: {
						error: this.analyticsEventsSessionData.json.repairJson.error,
						firstFailedAtChunk: this.analyticsEventsSessionData.json.repairJson.firstFailedAtChunk,
						lastFailedAtChunk: this.analyticsEventsSessionData.json.repairJson.lastFailedAtChunk,
					},
				});
			}
		}
		if (this.analyticsEventsSessionData.adf.failedToRepairADF.failed) {
			if (this.analyticsEventsSessionData.adf.failedToRepairADF.recovered) {
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Failed to repair ADF json during streaming, but recovered',
					data: {
						error: this.analyticsEventsSessionData.adf.failedToRepairADF.error,
						stack: this.analyticsEventsSessionData.adf.failedToRepairADF.stack,
						firstFailedAtChunk:
							this.analyticsEventsSessionData.adf.failedToRepairADF.firstFailedAtChunk,
						lastFailedAtChunk:
							this.analyticsEventsSessionData.adf.failedToRepairADF.lastFailedAtChunk,
						recovered: this.analyticsEventsSessionData.adf.failedToRepairADF.recovered,
						firstRecoveredAtChunk:
							this.analyticsEventsSessionData.adf.failedToRepairADF.firstRecoveredAtChunk,
						lastRecoveredAtChunk:
							this.analyticsEventsSessionData.adf.failedToRepairADF.lastRecoveredAtChunk,
					},
				});
			} else {
				this.fireAnalyticEvent({
					type: 'error',
					message: 'Failed to repair ADF Json',
					data: {
						error: this.analyticsEventsSessionData.adf.failedToRepairADF.error,
						stack: this.analyticsEventsSessionData.adf.failedToRepairADF.stack,
						firstFailedAtChunk:
							this.analyticsEventsSessionData.adf.failedToRepairADF.firstFailedAtChunk,
						lastFailedAtChunk:
							this.analyticsEventsSessionData.adf.failedToRepairADF.lastFailedAtChunk,
					},
				});
			}
		}
		if (this.analyticsEventsSessionData.adf.topNode.failed) {
			if (this.analyticsEventsSessionData.adf.topNode.recovered) {
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Got invalid top not, but recovered',
					data: {
						error: this.analyticsEventsSessionData.adf.topNode.error,
						firstFailedAtChunk: this.analyticsEventsSessionData.adf.topNode.firstFailedAtChunk,
						lastFailedAtChunk: this.analyticsEventsSessionData.adf.topNode.lastFailedAtChunk,
						firstRecoveredAtChunk:
							this.analyticsEventsSessionData.adf.topNode.firstRecoveredAtChunk,
						lastRecoveredAtChunk: this.analyticsEventsSessionData.adf.topNode.lastRecoveredAtChunk,
					},
				});
			} else {
				this.fireAnalyticEvent({
					type: 'error',
					message: 'Invalid top node',
					data: {
						error: this.analyticsEventsSessionData.adf.topNode.error,
						firstFailedAtChunk: this.analyticsEventsSessionData.adf.topNode.firstFailedAtChunk,
						lastFailedAtChunk: this.analyticsEventsSessionData.adf.topNode.lastFailedAtChunk,
					},
				});
			}
		}

		if (this.analyticsEventsSessionData.adf.topNodeValidation.failed) {
			if (this.analyticsEventsSessionData.adf.topNodeValidation.recovered) {
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Top node validation failed, but recovered',
					data: {
						error: this.analyticsEventsSessionData.adf.topNodeValidation.error,
						validationError: this.analyticsEventsSessionData.adf.topNodeValidation.validationError,
						firstFailedAtChunk:
							this.analyticsEventsSessionData.adf.topNodeValidation.firstFailedAtChunk,
						lastFailedAtChunk:
							this.analyticsEventsSessionData.adf.topNodeValidation.lastFailedAtChunk,
						firstRecoveredAtChunk:
							this.analyticsEventsSessionData.adf.topNodeValidation.firstRecoveredAtChunk,
						lastRecoveredAtChunk:
							this.analyticsEventsSessionData.adf.topNodeValidation.lastRecoveredAtChunk,
					},
				});
			} else {
				this.fireAnalyticEvent({
					type: 'error',
					message: 'Top node validation failed',
					data: {
						error: this.analyticsEventsSessionData.adf.topNodeValidation.error,
						validationError: this.analyticsEventsSessionData.adf.topNodeValidation.validationError,
						firstFailedAtChunk:
							this.analyticsEventsSessionData.adf.topNodeValidation.firstFailedAtChunk,
						lastFailedAtChunk:
							this.analyticsEventsSessionData.adf.topNodeValidation.lastFailedAtChunk,
					},
				});
			}
		}

		this.analyticsEventsSessionData.adf.repairInvalidContent.wrappingNodesFoundEvents.forEach(
			(event) => {
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Wrapping nodes found',
					data: { ...event },
				});
			},
		);
		this.analyticsEventsSessionData.adf.repairInvalidContent.nodeMovedToEndOfPreviousNodeEvents.forEach(
			(event) => {
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Node moved to the end of previous node',
					data: { ...event },
				});
			},
		);
		this.analyticsEventsSessionData.adf.repairInvalidContent.invalidWrappingNodesEvents.forEach(
			(event) => {
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Invalid wrapping nodes found for node.',
					data: { ...event },
				});
			},
		);
		this.analyticsEventsSessionData.adf.repairInvalidContent.wrappingNodesNotFoundEvents.forEach(
			(event) => {
				this.fireAnalyticEvent({
					type: 'info',
					message: 'Wrapping nodes not found.',
					data: { ...event },
				});
			},
		);

		// Derive a compact repair-signal summary that callers (e.g. the streaming
		// orchestrator) can use to classify the overall session outcome. Gated
		// behind `platform_editor_ai_normalized_telemetry` so the function is a
		// pure no-op (return `undefined`) for callers when the experiment is
		// off, preserving the original `void`-style behaviour bit-for-bit.
		if (!expValEquals('platform_editor_ai_normalized_telemetry', 'isEnabled', true)) {
			return undefined;
		}

		const finalJsonInvalid = this.analyticsEventsSessionData.json.repairJson.failed;
		const finalJsonRepaired =
			finalJsonInvalid && this.analyticsEventsSessionData.json.repairJson.recovered;
		const failedToRepairADFInvalid = this.analyticsEventsSessionData.adf.failedToRepairADF.failed;
		const topNodeValidationInvalid = this.analyticsEventsSessionData.adf.topNodeValidation.failed;
		const finalAdfInvalid = failedToRepairADFInvalid || topNodeValidationInvalid;
		// finalAdfRepaired is true iff every layer that failed also recovered.
		const failedToRepairADFRecovered = failedToRepairADFInvalid
			? this.analyticsEventsSessionData.adf.failedToRepairADF.recovered
			: true;
		const topNodeValidationRecovered = topNodeValidationInvalid
			? this.analyticsEventsSessionData.adf.topNodeValidation.recovered
			: true;
		const finalAdfRepaired =
			finalAdfInvalid && failedToRepairADFRecovered && topNodeValidationRecovered;

		return {
			finalJsonInvalid,
			finalJsonRepaired,
			finalAdfInvalid,
			finalAdfRepaired,
		};
	}
}

type AnalyticEventArgs =
	| {
			data: { chunksCount: number; inputDocumentTree?: NodeTypeTree };
			message: 'Number of chunks processed';
			type: 'info';
	  }
	| {
			data: { error: string; firstFailedAtChunk: number; lastFailedAtChunk: number };
			message: 'Received invalid json';
			type: 'error';
	  }
	| {
			data: { inputDocumentTree?: NodeTypeTree };
			message: 'Top node is empty';
			type: 'error';
	  }
	| {
			data: { error: string };
			message:
				| 'Deserializing last doc to node failed'
				| 'Deserializing last doc to node failed in isInErrorState';
			type: 'error';
	  }
	| {
			data: {
				error: string;
				firstFailedAtChunk: number;
				lastFailedAtChunk: number;
			};
			message: 'Invalid top node';
			type: 'error';
	  }
	| {
			data: {
				error: string;
				firstFailedAtChunk: number;
				firstRecoveredAtChunk: number;
				lastFailedAtChunk: number;
				lastRecoveredAtChunk: number;
			};
			message: 'Got invalid top not, but recovered';
			type: 'info';
	  }
	| {
			data: {
				error: string;
				firstFailedAtChunk: number;
				firstRecoveredAtChunk: number;
				lastFailedAtChunk: number;
				lastRecoveredAtChunk: number;
				recovered: boolean;
			};
			message: 'Failed to repair partial JSON using jsonrepair';
			type: 'info';
	  }
	| {
			data: {
				nodeType: string;
				originalNodeTypeTree: NodeTypeTree;
				repairedContentNodeTypeTree: NodeTypeTree[];
			};
			message: 'Tried to repair content of node, but failed.';
			type: 'info';
	  }
	| {
			data: {
				error: string;
				firstFailedAtChunk: number;
				lastFailedAtChunk: number;
				validationError?: ValidationError;
			};
			message: 'Top node validation failed';
			type: 'error';
	  }
	| {
			data: {
				error: string;
				firstFailedAtChunk: number;
				firstRecoveredAtChunk: number;
				lastFailedAtChunk: number;
				lastRecoveredAtChunk: number;
				validationError?: ValidationError;
			};
			message: 'Top node validation failed, but recovered';
			type: 'info';
	  }
	| {
			data: { error: ValidationError | string; nodeType: string };
			message: 'Node validation failed';
			type: 'info';
	  }
	| { data: { nodeType: string }; message: 'Failed to infer node type.'; type: 'info' }
	| {
			data: {
				childIndex: number;
				childNodeType: string;
				parentNodeType: string;
				wrappingNodeTypes: string[];
			};
			message: 'Wrapping nodes found';
			type: 'info';
	  }
	| {
			data: {
				childIndex: number;
				childNodeType: string;
				currentChildNode: string;
				wrappingNodeType: string;
			};
			message: 'Invalid wrapping nodes found for node.';
			type: 'info';
	  }
	| {
			data: { childIndex: number; childNodeType: string; parentNodeType: string };
			message: 'Wrapping nodes not found.';
			type: 'info';
	  }
	| {
			data: {
				error: string;
				firstFailedAtChunk: number;
				firstRecoveredAtChunk: number;
				lastFailedAtChunk: number;
				lastRecoveredAtChunk: number;
				recovered: boolean;
				stack?: string;
			};
			message: 'Failed to repair ADF json during streaming, but recovered';
			type: 'info';
	  }
	| {
			data: {
				error: string;
				firstFailedAtChunk: number;
				lastFailedAtChunk: number;
				stack?: string;
			};
			message: 'Failed to repair ADF Json';
			type: 'error';
	  }
	| {
			data: {
				childIndex: number;
				error: string;
				nodeType: string;
				parentNodeType: string;
				stack?: string;
			};
			message: 'Failed to repair node';
			type: 'info';
	  }
	| { data: { markType: string }; message: 'Failed to repair mark, type not found'; type: 'info' }
	| {
			data: {
				childIndexInPreviousNode: number;
				movedNodeType: string;
				oldChildIndex: number;
				parentNodeType: string;
				previousNodeType: string;
			};
			message: 'Node moved to the end of previous node';
			type: 'info';
	  };

export type FireAnalyticEventCallback = (args: AnalyticEventArgs) => void;
